// S6.5 Phase 3 — humanize a permission_request into founder-mode copy.
//
// Inputs:  toolName + raw toolInput  (and an optional `classifierReason`
//          string that the CLI may have produced).
// Output:  { headline, body, risk } — pure plain-English; NEVER contains
//          raw shell, JSON, file paths beginning with `/`, or jargon like
//          `tool_use_id` / `permission_request` / `tool_result`.
//
// Pure function. No React, no DOM, no fetch. Tested via fixture table.

export type PermissionRisk = 'low' | 'medium' | 'high';

export interface HumanizedPermission {
  headline: string;
  body: string;
  risk: PermissionRisk;
}

interface HumanizeOpts {
  toolName: string;
  toolInput: unknown;
  classifierReason?: string;
  agentName?: string;
}

// founder-first §8 filter: any string we surface in headline or body must
// be safe to show a non-developer. We strip the most common code-shapes
// when we have to fall through to a generic catch-all.
function safe(s: string, max = 120): string {
  const trimmed = s.replace(/\s+/g, ' ').trim();
  return trimmed.length > max ? trimmed.slice(0, max - 1) + '…' : trimmed;
}

// Returns the first non-empty string from a record at any of the given
// keys. Helper for tools whose input field name has shifted across CLI
// versions (e.g. Bash uses `command` today; Read uses `file_path`).
function getString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
}

function basename(p: string): string {
  const trimmed = p.replace(/\/+$/, '');
  const i = trimmed.lastIndexOf('/');
  return i === -1 ? trimmed : trimmed.slice(i + 1);
}

function dirname(p: string): string {
  const trimmed = p.replace(/\/+$/, '');
  const i = trimmed.lastIndexOf('/');
  return i === -1 ? '' : trimmed.slice(0, i) || '/';
}

// Bash command classifier — returns one of a fixed set of "intents" so the
// headline/body/risk derivation can match per-intent. Order matters: the
// first match wins.
type BashIntent =
  | { kind: 'rm-recursive'; target: string }
  | { kind: 'rm'; target: string }
  | { kind: 'git-push'; remote: string; branch: string }
  | { kind: 'git-reset-hard'; ref: string }
  | { kind: 'git-checkout-force' }
  | { kind: 'chmod' }
  | { kind: 'chown' }
  | { kind: 'sudo' }
  | { kind: 'curl-download'; host: string }
  | { kind: 'wget-download'; host: string }
  | { kind: 'pkg-install'; tool: string; pkg: string }
  | { kind: 'kill-process' }
  | { kind: 'generic'; firstWord: string };

function parseBashIntent(cmd: string): BashIntent {
  const trimmed = cmd.trim();

  if (/(^|\s|;|&&|\|\|)\s*rm\s+(?:-[a-zA-Z]*r[a-zA-Z]*|--recursive)/.test(trimmed)) {
    const target = trimmed.match(/rm\s+(?:-\S+\s+)*(\S+)/)?.[1] ?? '';
    return { kind: 'rm-recursive', target };
  }
  if (/(^|\s|;|&&|\|\|)\s*rm\s+/.test(trimmed)) {
    const target = trimmed.match(/rm\s+(?:-\S+\s+)*(\S+)/)?.[1] ?? '';
    return { kind: 'rm', target };
  }
  const gitPush = trimmed.match(/^git\s+push(?:\s+(\S+))?(?:\s+(\S+))?/);
  if (gitPush) {
    return { kind: 'git-push', remote: gitPush[1] ?? 'origin', branch: gitPush[2] ?? 'HEAD' };
  }
  const gitReset = trimmed.match(/^git\s+reset\s+--hard(?:\s+(\S+))?/);
  if (gitReset) return { kind: 'git-reset-hard', ref: gitReset[1] ?? 'HEAD' };
  if (/^git\s+checkout\s+-(?:f|-force)/.test(trimmed)) return { kind: 'git-checkout-force' };
  if (/^chmod\b/.test(trimmed)) return { kind: 'chmod' };
  if (/^chown\b/.test(trimmed)) return { kind: 'chown' };
  if (/^sudo\b/.test(trimmed)) return { kind: 'sudo' };
  const curl = trimmed.match(/^curl\s+(?:.*?\s)?(?:https?:\/\/)?([^\s/]+)/);
  if (/^curl\b/.test(trimmed)) return { kind: 'curl-download', host: curl?.[1] ?? '' };
  const wget = trimmed.match(/^wget\s+(?:.*?\s)?(?:https?:\/\/)?([^\s/]+)/);
  if (/^wget\b/.test(trimmed)) return { kind: 'wget-download', host: wget?.[1] ?? '' };
  const pkg = trimmed.match(/^(npm|yarn|pnpm|pip|brew|apt(?:-get)?|cargo)\s+(?:install|add|i)\s+(\S+)/);
  if (pkg) return { kind: 'pkg-install', tool: pkg[1] ?? '', pkg: pkg[2] ?? '' };
  if (/^(kill|pkill)\b/.test(trimmed)) return { kind: 'kill-process' };
  return { kind: 'generic', firstWord: trimmed.split(/\s+/, 1)[0] ?? '' };
}

function humanizeBash(cmd: string, actor: string): HumanizedPermission {
  const intent = parseBashIntent(cmd);
  switch (intent.kind) {
    case 'rm-recursive':
      return {
        headline: intent.target
          ? `${actor} wants to delete the folder ${safe(basename(intent.target), 40)}`
          : `${actor} wants to delete a folder`,
        body: intent.target ? `Inside ${safe(intent.target, 80)}.` : '',
        risk: 'high',
      };
    case 'rm':
      return {
        headline: intent.target
          ? `${actor} wants to delete ${safe(basename(intent.target), 40)}`
          : `${actor} wants to delete files`,
        body: intent.target ? `Inside ${safe(dirname(intent.target) || '.', 80)}.` : '',
        risk: 'high',
      };
    case 'git-push':
      return {
        headline: `${actor} wants to push to GitHub`,
        body: `Push ${safe(intent.branch, 60)} to ${safe(intent.remote, 40)}.`,
        risk: 'medium',
      };
    case 'git-reset-hard':
      return {
        headline: `${actor} wants to discard local changes`,
        body: `Hard-reset to ${safe(intent.ref, 60)}. Any uncommitted edits will be lost.`,
        risk: 'high',
      };
    case 'git-checkout-force':
      return {
        headline: `${actor} wants to discard local changes`,
        body: 'Force-checkout — any uncommitted edits will be lost.',
        risk: 'high',
      };
    case 'chmod':
      return {
        headline: `${actor} wants to change file permissions`,
        body: '',
        risk: 'medium',
      };
    case 'chown':
      return {
        headline: `${actor} wants to change file ownership`,
        body: '',
        risk: 'medium',
      };
    case 'sudo':
      return {
        headline: `${actor} wants to run a command as administrator`,
        body: 'sudo elevates privileges system-wide.',
        risk: 'high',
      };
    case 'curl-download':
      return {
        headline: intent.host
          ? `${actor} wants to download from ${safe(intent.host, 60)}`
          : `${actor} wants to download from the internet`,
        body: '',
        risk: 'medium',
      };
    case 'wget-download':
      return {
        headline: intent.host
          ? `${actor} wants to download from ${safe(intent.host, 60)}`
          : `${actor} wants to download from the internet`,
        body: '',
        risk: 'medium',
      };
    case 'pkg-install':
      return {
        headline: `${actor} wants to install ${safe(intent.pkg, 60)}`,
        body: `Using ${intent.tool}.`,
        risk: 'medium',
      };
    case 'kill-process':
      return {
        headline: `${actor} wants to stop a running process`,
        body: '',
        risk: 'medium',
      };
    case 'generic': {
      // founder-first §8: never put raw bash in the headline. Use the first
      // word as a humanized "verb" and keep the body intentionally vague.
      const verb = intent.firstWord || 'a command';
      return {
        headline: `${actor} wants to run a command`,
        body: `Starts with ${safe(verb, 40)}. Open developer details to see the exact command.`,
        risk: 'low',
      };
    }
  }
}

export function humanizePermissionRequest(opts: HumanizeOpts): HumanizedPermission {
  const { toolName, toolInput, classifierReason, agentName } = opts;
  const actor = agentName || 'Agent';

  // 1) classifier reason (if the CLI ever ships one) wins. We trust the
  // model's prose because it's already founder-friendly and self-aware
  // about why this particular call was flagged.
  if (classifierReason && classifierReason.trim().length > 0) {
    return {
      headline: safe(classifierReason, 100),
      body: '',
      risk: 'medium',
    };
  }

  const input =
    toolInput && typeof toolInput === 'object' ? (toolInput as Record<string, unknown>) : {};

  switch (toolName) {
    case 'Bash': {
      const cmd = getString(input, ['command']);
      if (!cmd) {
        return {
          headline: `${actor} wants to run a command`,
          body: 'Open developer details to see the exact command.',
          risk: 'low',
        };
      }
      return humanizeBash(cmd, actor);
    }

    case 'Edit':
    case 'Write':
    case 'MultiEdit':
    case 'NotebookEdit': {
      const filePath = getString(input, ['file_path', 'notebook_path']);
      if (!filePath) {
        return {
          headline: `${actor} wants to modify a file`,
          body: '',
          risk: 'low',
        };
      }
      const fname = basename(filePath);
      const dir = dirname(filePath);
      return {
        headline: `${actor} wants to modify ${safe(fname, 60)}`,
        body: dir ? `Inside ${safe(dir, 80)}.` : '',
        risk: 'low',
      };
    }

    case 'WebFetch': {
      const url = getString(input, ['url']);
      let host = '';
      if (url) {
        try {
          host = new URL(url).hostname;
        } catch {
          host = '';
        }
      }
      return {
        headline: host
          ? `${actor} wants to fetch from ${safe(host, 60)}`
          : `${actor} wants to fetch from the internet`,
        body: '',
        risk: 'low',
      };
    }

    case 'WebSearch': {
      const q = getString(input, ['query']);
      return {
        headline: `${actor} wants to search the web`,
        body: q ? `For "${safe(q, 80)}".` : '',
        risk: 'low',
      };
    }

    case 'ExitPlanMode': {
      return {
        headline: `${actor} finished planning and wants to start`,
        body: 'Approve to switch from Plan mode to Agent mode for the rest of this turn.',
        risk: 'low',
      };
    }

    default: {
      // Unknown / new tool. Keep generic — never leak the raw input.
      return {
        headline: `${actor} wants to run a sensitive action`,
        body: `Tool: ${safe(toolName, 40)}. Open developer details to see the exact request.`,
        risk: 'low',
      };
    }
  }
}
